import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { Search, ZoomOut, RefreshCw } from 'lucide-react';
import { logsService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import SegmentedControl from '../components/SegmentedControl';
import { LoadingState } from '../components/PageState';
import { useChartTheme } from '../components/chartTheme';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const PRESETS = [
  { label: '15m', ms: 15 * MINUTE },
  { label: '1h', ms: HOUR },
  { label: '4h', ms: 4 * HOUR },
  { label: '24h', ms: DAY },
  { label: '3d', ms: 3 * DAY },
  { label: '7d', ms: 7 * DAY },
];
const DEFAULT_PRESET = '1h';
const LEVELS = [
  { value: 'ALL', label: 'All' },
  { value: 'ERROR', label: 'Error' },
  { value: 'WARN', label: 'Warning' },
  { value: 'INFO', label: 'Info' },
];
const LEVEL_VARIANT = { ERROR: 'danger', WARN: 'warning', INFO: 'secondary', DEBUG: 'secondary', TRACE: 'secondary' };
const MIN_ZOOM_BUCKET_MS = 10 * 1000;

const pad = (n, width = 2) => String(n).padStart(width, '0');

const toLocalInput = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) => {
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const parseTime = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

// The window to open with: a custom or zoomed window from ?from= and ?to=, otherwise a preset
const initialWindow = (params) => {
  const from = parseTime(params.get('from'));
  const to = parseTime(params.get('to'));
  if (params.get('preset') === 'custom' && from != null && to != null && from < to) {
    return { preset: 'custom', range: { from, to } };
  }
  const preset = PRESETS.some((p) => p.label === params.get('preset')) ? params.get('preset') : DEFAULT_PRESET;
  const now = Date.now();
  const ms = PRESETS.find((p) => p.label === preset).ms;
  return { preset, range: { from: now - ms, to: now } };
};

// A drag across a line selects its text rather than opening it
const hasSelection = () => !!window.getSelection && String(window.getSelection()).length > 0;

const fmtClock = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const fmtDay = (ms) => {
  const d = new Date(ms);
  return `${d.getDate()} ${d.toLocaleString(undefined, { month: 'short' })}`;
};

const fmtDateTime = (ms) => {
  const d = new Date(ms);
  return `${fmtDay(ms)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const fmtTick = (ms, rangeMs, bucketMs) => {
  const d = new Date(ms);
  if (rangeMs > DAY) {
    return `${d.getDate()} ${d.toLocaleString(undefined, { month: 'short' })} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  if (bucketMs < MINUTE) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtBytes = (b) => {
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${Math.round(b / 1e3)} KB`;
};

const shortLogger = (logger) => (logger ? logger.substring(logger.lastIndexOf('.') + 1) : '');
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlight = ({ text, term }) => {
  if (!term || !text) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'ig'));
  return parts.map((part, i) => (
    part.toLowerCase() === term.toLowerCase()
      ? <mark key={i} className="logs-mark">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
};

const eventKey = (ev, idx) => `${ev.stream}|${ev.timestamp}|${idx}`;

const Logs = () => {
  const ensureToken = useAuthToken();
  const isMobile = useIsMobile();
  const chartTheme = useChartTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initial] = useState(() => initialWindow(searchParams));

  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [source, setSource] = useState(searchParams.get('source') || '');
  const [text, setText] = useState(searchParams.get('q') || '');
  const [level, setLevel] = useState(() => (LEVELS.some((l) => l.value === searchParams.get('level')) ? searchParams.get('level') : 'ALL'));
  const [preset, setPreset] = useState(initial.preset);
  const [range, setRange] = useState(initial.range);
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newestFirst, setNewestFirst] = useState(true);
  const [contexts, setContexts] = useState({});
  const requestId = useRef(0);

  useEffect(() => {
    const loadSources = async () => {
      try {
        await ensureToken();
        const res = await logsService.getSources();
        setSources(res.data);
        if (!res.data.some((s) => s.key === source) && res.data.length > 0) {
          setSource(res.data[0].key);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load log sources');
      } finally {
        setSourcesLoading(false);
      }
    };
    loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Runs a search with the current controls, or with any of them overridden. Overrides exist so
   * click handlers can change a control and search in the same tick without waiting for state.
   */
  const runSearch = useCallback(async (overrides = {}) => {
    const s = { source, text, level, preset, range, ...overrides };
    if (!s.source) return;

    let { from, to } = s.range;
    if (s.preset !== 'custom') {
      const ms = PRESETS.find((p) => p.label === s.preset)?.ms ?? HOUR;
      to = Date.now();
      from = to - ms;
    } else if (from >= to) {
      setError('The window must start before it ends.');
      return;
    }
    setRange({ from, to });
    setContexts({});
    setError(null);
    setLoading(true);
    const id = ++requestId.current;

    try {
      await ensureToken();
      const res = await logsService.search({
        source: s.source, from, to, text: s.text.trim(), level: s.level,
      });
      if (id !== requestId.current) return;
      setResult(res.data);
      // A custom or zoomed window keeps its exact ends, so a reload or a shared link shows the same lines
      const params = { source: s.source, q: s.text.trim(), level: s.level, preset: s.preset };
      if (s.preset === 'custom') {
        params.from = new Date(from).toISOString();
        params.to = new Date(to).toISOString();
      }
      setSearchParams(params, { replace: true });
    } catch (err) {
      if (id !== requestId.current) return;
      setResult(null);
      setError(err.response?.data?.error || 'Failed to query logs');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [source, text, level, preset, range, ensureToken, setSearchParams]);

  useEffect(() => {
    if (source) runSearch({ source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const handlePreset = (label) => {
    setPreset(label);
    setHistory([]);
    runSearch({ preset: label });
  };

  const handleLevel = (value) => {
    setLevel(value);
    runSearch({ level: value });
  };

  const handleCustomRange = (field, value) => {
    const ms = fromLocalInput(value);
    if (ms == null) return;
    setPreset('custom');
    setRange((r) => ({ ...r, [field]: ms }));
  };

  const zoomTo = (bucket) => {
    if (!result || result.bucketMs <= MIN_ZOOM_BUCKET_MS) return;
    setHistory((h) => [...h, { from: range.from, to: range.to, preset }]);
    setPreset('custom');
    runSearch({ preset: 'custom', range: { from: bucket.start, to: bucket.start + result.bucketMs } });
  };

  const zoomOut = () => {
    const previous = history[history.length - 1];
    if (!previous) return;
    setHistory((h) => h.slice(0, -1));
    setPreset(previous.preset);
    runSearch({ preset: previous.preset, range: { from: previous.from, to: previous.to } });
  };

  const toggleContext = async (ev, key) => {
    if (contexts[key]) {
      setContexts((c) => { const next = { ...c }; delete next[key]; return next; });
      return;
    }
    setContexts((c) => ({ ...c, [key]: { loading: true } }));
    try {
      await ensureToken();
      const res = await logsService.getContext({ source, stream: ev.stream, timestamp: ev.timestamp });
      setContexts((c) => (c[key] ? { ...c, [key]: { events: res.data } } : c));
    } catch (err) {
      setContexts((c) => (c[key] ? { ...c, [key]: { error: err.response?.data?.error || 'Failed to load context' } } : c));
    }
  };

  const events = useMemo(() => {
    if (!result) return [];
    return newestFirst ? result.events : [...result.events].slice().reverse();
  }, [result, newestFirst]);

  const rangeMs = range.to - range.from;
  const term = text.trim();
  const canZoom = result && result.bucketMs > MIN_ZOOM_BUCKET_MS;

  const handleChartClick = (state) => {
    if (!result || !canZoom) return;
    const start = state?.activePayload?.[0]?.payload?.start ?? state?.activeLabel;
    const bucket = result.histogram.find((b) => b.start === start);
    if (bucket) zoomTo(bucket);
  };

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const b = payload[0].payload;
    return (
      <div className="logs-tooltip">
        <div className="logs-tooltip-title">{fmtDateTime(b.start)} to {fmtClock(b.start + result.bucketMs).slice(0, 8)}</div>
        <div>{b.count.toLocaleString()} {b.count === 1 ? 'line' : 'lines'}</div>
        {canZoom && <div className="text-muted">Click to zoom in</div>}
      </div>
    );
  };

  const currentSource = sources.find((s) => s.key === source);

  const header = (
    <Row className="mb-3">
      <Col>
        <h1>Logs</h1>
        <p className="text-muted mb-0">
          Application output (web.stdout.log) from one Elastic Beanstalk environment at a time, read from CloudWatch Logs.
        </p>
      </Col>
    </Row>
  );

  if (sourcesLoading) {
    return (
      <Container fluid className="py-4">
        {header}
        <LoadingState label="Loading environments" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {header}

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Form onSubmit={(e) => { e.preventDefault(); setHistory([]); runSearch(); }}>
            <Row className="g-3 align-items-end">
              <Col md={6} lg={3}>
                <Form.Label htmlFor="logs-source">Environment</Form.Label>
                <Form.Select id="logs-source" value={source} onChange={(e) => { setHistory([]); setSource(e.target.value); }}>
                  {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={6} lg={5}>
                <Form.Label htmlFor="logs-text">Filter text</Form.Label>
                <Form.Control
                  id="logs-text"
                  type="text"
                  placeholder="Email, company id, exception name, request path..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </Col>
              <Col md={12} lg={4}>
                <Form.Label as="div">Level</Form.Label>
                <div>
                  <SegmentedControl
                    options={LEVELS}
                    value={level}
                    onChange={handleLevel}
                    size={isMobile ? undefined : 'sm'}
                    ariaLabel="Level"
                  />
                </div>
              </Col>
            </Row>
            <Row className="g-3 align-items-end mt-1">
              <Col md="auto">
                <Form.Label as="div">Last</Form.Label>
                <div>
                  <SegmentedControl
                    options={PRESETS.map((p) => ({ value: p.label, label: p.label }))}
                    value={preset}
                    onChange={handlePreset}
                    size={isMobile ? undefined : 'sm'}
                    ariaLabel="Time window"
                  />
                </div>
              </Col>
              <Col xs={6} md="auto">
                <Form.Label htmlFor="logs-from">From</Form.Label>
                <Form.Control
                  id="logs-from"
                  type="datetime-local"
                  size="sm"
                  value={toLocalInput(range.from)}
                  onChange={(e) => handleCustomRange('from', e.target.value)}
                />
              </Col>
              <Col xs={6} md="auto">
                <Form.Label htmlFor="logs-to">To</Form.Label>
                <Form.Control
                  id="logs-to"
                  type="datetime-local"
                  size="sm"
                  value={toLocalInput(range.to)}
                  onChange={(e) => handleCustomRange('to', e.target.value)}
                />
              </Col>
              {/* One Search after every control, so edited dates and text apply together */}
              <Col md="auto" className="ms-auto d-flex align-items-center gap-2">
                <Button size="sm" variant="outline-secondary" onClick={zoomOut} disabled={loading || history.length === 0}>
                  <ZoomOut size={14} className="me-1" />Zoom out
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  className="text-muted p-1"
                  onClick={() => runSearch()}
                  disabled={loading || !source}
                  aria-label="Refresh"
                  title="Refresh: run the search again"
                >
                  <RefreshCw size={16} aria-hidden="true" />
                </Button>
                <Button type="submit" size={isMobile ? undefined : 'sm'} variant="primary" disabled={loading || !source}>
                  <Search size={14} className="me-1" />Search
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card className="shadow-sm mb-3">
        <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center column-gap-3">
          <h5 className="mb-0">Timeline</h5>
          <span className="text-muted small">
            {fmtDateTime(range.from)} to {fmtDateTime(range.to)}
            {result && ` · ${result.bucketMs >= MINUTE ? `${result.bucketMs / MINUTE} min` : `${result.bucketMs / 1000} s`} buckets`}
            {canZoom && ' · click a bar to zoom in'}
          </span>
        </Card.Header>
        <Card.Body className="position-relative">
          {loading && (
            <div className="logs-overlay">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={result?.histogram ?? []}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: canZoom ? 'pointer' : 'default' }}
            >
              <CartesianGrid {...chartTheme.grid} />
              <XAxis
                {...chartTheme.xAxis}
                dataKey="start"
                tickFormatter={(v) => fmtTick(v, rangeMs, result?.bucketMs ?? MINUTE)}
                minTickGap={40}
                axisLine={false}
              />
              <YAxis {...chartTheme.yAxis} allowDecimals={false} width={44} />
              <Tooltip cursor={chartTheme.tooltip.cursor} content={renderTooltip} />
              <Bar dataKey="count" fill={chartTheme.colors.emphasis} radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            Lines
            {result && (
              <span className="text-muted small fw-normal ms-2">
                {result.recordsMatched > result.events.length
                  ? `latest ${result.events.length.toLocaleString()} of ${result.recordsMatched.toLocaleString()} matches`
                  : `${result.events.length.toLocaleString()} ${result.events.length === 1 ? 'match' : 'matches'}`}
                {` · scanned ${result.recordsScanned.toLocaleString()} lines (${fmtBytes(result.bytesScanned)}) in ${(result.queryMs / 1000).toFixed(1)}s`}
              </span>
            )}
          </h5>
          <Form.Check
            type="switch"
            id="logs-newest-first"
            label="Newest first"
            checked={newestFirst}
            onChange={(e) => setNewestFirst(e.target.checked)}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {!result && !loading && !error && (
            <p className="text-muted p-3 mb-0">Pick an environment and a window to start.</p>
          )}
          {result && events.length === 0 && (
            <p className="text-muted p-3 mb-0">
              No lines matched in this window{currentSource ? ` on ${currentSource.environment}` : ''}.
            </p>
          )}
          {events.length > 0 && (
            <div className="logs-list">
              {events.map((ev, idx) => {
                const key = eventKey(ev, idx);
                const ctx = contexts[key];
                return (
                  <React.Fragment key={key}>
                    {/* A button in all but tag, so the grid layout keeps its styles: Enter or Space opens it too */}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={!!ctx}
                      className={`logs-row${ev.level ? '' : ' logs-row-continuation'}${ctx ? ' logs-row-open' : ''}`}
                      onClick={() => { if (!hasSelection()) toggleContext(ev, key); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleContext(ev, key);
                        }
                      }}
                      title="Show the surrounding lines from this instance"
                    >
                      <span className="logs-time">{fmtDay(ev.timestamp)} {fmtClock(ev.timestamp)}</span>
                      <span className="logs-level">
                        {ev.level && (
                          <Badge bg={LEVEL_VARIANT[ev.level] || 'secondary'}>
                            {ev.level}
                          </Badge>
                        )}
                      </span>
                      <span className="logs-logger" title={ev.logger || ''}>{shortLogger(ev.logger)}</span>
                      <span className="logs-msg"><Highlight text={ev.message} term={term} /></span>
                      <span className="logs-stream" title={`Instance ${ev.stream}`}>{ev.stream?.slice(-5)}</span>
                    </div>
                    {ctx && (
                      <div className="logs-context">
                        {ctx.loading && <Spinner animation="border" size="sm" className="m-2" />}
                        {ctx.error && <span className="text-danger">{ctx.error}</span>}
                        {ctx.events && (
                          <>
                            <div className="logs-context-title text-muted">
                              Instance {ev.stream}, {fmtClock(ev.timestamp - 2000)} to {fmtClock(ev.timestamp + 5000)}
                            </div>
                            <pre className="logs-context-pre mb-0">
                              {ctx.events.map((c, i) => (
                                <div
                                  key={i}
                                  className={c.timestamp === ev.timestamp && c.raw === ev.raw ? 'logs-context-target' : undefined}
                                >
                                  {fmtClock(c.timestamp)}  {c.level ? `${c.level.padEnd(5)} ${shortLogger(c.logger)} : ${c.message}` : c.message}
                                </div>
                              ))}
                            </pre>
                          </>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Logs;
